import DEFAULT from "@/defaultValues.js";

// シミュレーター定数
const waveColor = '#333',
paddingRight = 90,
paddingLeft = 210;

// ワイングラス定数
const wgImage = "./assets/winegrass.svg",
  breakingSound = "./assets/break.mp3",
  sx = 0,
  sy = 0,
  sw = 420,
  sh = 440,
  wgWidth = 168,
  wgHeight = 176;

// 最大最小
const limit = (num, min, max) => {
  return Math.min(Math.max(num, min), max);
};

// ワイングラス
class Grass {
  /**
   * @param {float} dist 距離
   * @param {float} y    y座標
   * @param {Simulator} simulator シミュレーター本体
   */
  constructor(dist, y, simulator) {
    this.simulator = simulator;
    this.ctx = simulator.ctx;
    this.cmpp = simulator.cmpp;
    this.naturalFreq = simulator.naturalFreq;
    this.thresholdAmp = simulator.thresholdAmp;
    this.img = new Image();
    this.img.src = wgImage;

    this.promise = [];
    
    this.promise.push(new Promise(
      ((resolve) => {
        this.img.onload = resolve;
      }).bind(this)
    ));

    this.breakingSound = new Audio(breakingSound);
    this.breakingSound.preload = 'metadata';

    this.promise.push(
      new Promise(
        ((resolve) => {
          this.breakingSound.onloadedmetadata = resolve;
        }).bind(this)
      )
    );

    //this.breakingSound.controls = true;
    document.body.appendChild(this.breakingSound);

    this.sx = sx;
    this.sy = sy;
    this.sw = sw;
    this.sh = sh;
    this.w = wgWidth;
    this.h = wgHeight;
    this.dist = dist;
    this.y = y - this.h / 2;

    this.dx = 0; // ずれ

    this.startTime = 0; // 閾値を超えたときの開始時間
    this.isBroken = false;
  }
  load(fn) {
    Promise.all(this.promise).then(fn);
  }
  draw() {
    this.ctx.drawImage(this.img, this.sx, this.sy, this.sw, this.sh, this.x - this.w /2 + 27 + this.dx, this.y, this.w, this.h);
  }
  // 振動判定
  vibration(a) {
    if (this.isBroken) return;
    const param = this.simulator.getWaveParam(this.dist);
    if (param.freq && param.amp) {
      let k = 1;

      const deffFreq = Math.abs(this.naturalFreq - param.freq); // 固有振動数と音波の周波数の差

      if (deffFreq == 0) {
        k = 4;
      } else if (deffFreq <= 5) {
        k = 2.9;
      } else if (deffFreq <= 10) {
        k = 2.2;
      } else if (deffFreq <= 20) {
        k = 1.8;
      }

      if (param.freq == this.naturalFreq) {
        const amp = param.amp * this.simulator.attenuation(this.dist);

        if (amp - this.thresholdAmp >= 0) {
          if (!this.startTime) {
            this.startTime = performance.now();
          } else if (performance.now() - this.startTime >= 5000) {
            this.break();
          }
        } else {
          this.startTime = 0;
        }
      }

      this.dx = Math.sin(a * this.naturalFreq) * k;
    } else {
      this.startTime = 0;
    }
  }
  // 破壊処理
  break() {
    this.isBroken = true;
    this.startTime = 0;
    setTimeout(() => {
      this.sy = sh;
      setTimeout(() => {
        this.sy = sh * 2;
        setTimeout(() => {
          this.sy = sh * 3;
        }, 100);
      }, 100);
    }, 100);
    this.breakingSound.currentTime = 0;
    this.breakingSound.play();
    this.simulator.break();
  }
  preSound() {
    this.breakingSound.muted = true;
    this.breakingSound.play();
    setTimeout(() => {
      this.breakingSound.pause();
      this.breakingSound.muted = false;
      this.preSound = false;
    }, 100)
  }
  set dist(val) {
    this._dist = val;
    this.x = val / this.cmpp;
  }
  get dist() {
    return this._dist;
  }
}

// シミュレーター
class Simulator {
  constructor(selector = "canvas", opt = {}) {
    const canvas = document.querySelector(selector);

    if (!canvas) {
      alert("エラー！　初期化に失敗しました");
      throw "There is no Canvas!";
    }
    this.canvas = canvas;
    this.ctx = this.canvas.getContext("2d");

    this.waveColor = waveColor;
    this.drawLine = false;

    // サウンド初期化
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    this.gainNode = this.audioCtx.createGain();

    this.gainNode.connect(this.audioCtx.destination);
    this.gainNode.gain.value = 0.1;

    // パラメータ設定
    opt = Object.assign({}, DEFAULT, opt);

    Object.keys(DEFAULT).forEach((key) => {
      this[key] = opt[key];
    });
    if (this.minFreq < 0) this.minFreq = 0;

    this.speed *= 100; // 単位を [cm/s] に変換

    this.setup = false;
    this.isStarting = false;
    this.isPaused = false;
    this.startTime;
    this.pausedTime;
    this.requestID;

    this.lastFreq = false; // 前回描画時の周波数
    this.freqList = []; // 周波数変化のリスト
    this.freqCounter = 0;

    this.lastAmp = false; // 前回描画時の振幅
    this.ampList = []; // 振幅変化のリスト
    this.ampCounter = 0;
    this.currentAmp; // 処理中の振幅

    this.paramList = []; // 波形変化のリスト

    // canvas初期化
    const dpr = window.devicePixelRatio || 1; // デバイスピクセルレシオ

    this.width = this.canvas.width; // Canvas横幅
    this.height = this.canvas.height; // Canvas縦幅

    this.maxX = this.width - paddingLeft; // X座標最大値 [px]
    this.cmpp = this.maxDist / (this.width - (paddingLeft + paddingRight)); // 距離とピクセルの比 [cm/px]

    /** 高解像度対応 */
    // Canvasをピクセル比で拡大
    this.canvas.width *= dpr;
    this.canvas.height *= dpr;
    // CSS
    this.canvas.style.maxWidth = this.width + "px";
    // Canvasの描画自体を拡大
    this.ctx.scale(dpr, dpr);

    /** 座標系の設定 */
    // y座標を反転
    //ctx.scale(1, -1);
    // x軸, y軸に沿って原点が中心にくるようにずらす
    this.ctx.translate(paddingLeft, this.height / 2);

    this.ctx.strokeStyle = this.waveColor;
    this.ctx.lineWidth = 2;

    // ワイングラス
    this.grassSet();
  }
  // シミュレーション開始
  start() {
    if (this.isStarting || !this.setup) return;

    this.isStarting = true;
    this.isPaused = false;
    this.lastFreq = this.freq;
    this.lastAmp = this.amp;
    this.paramList = [];
    this.makeOscillator();
    this.oscillator.start(0);

    if (this.grass.preSound) this.grass.preSound();

    this.requestID = requestAnimationFrame(this.tick.bind(this));
  }
  restart() {
    if (!this.isStarting || !this.isPaused) return;

    this.isPaused = false;
    this.makeOscillator();
    this.oscillator.start(0);

    this.startTime += performance.now() - this.pausedTime;

    this.requestID = requestAnimationFrame(this.tick.bind(this));
  }
  // シミュレーション停止
  stop() {
    if (!this.isStarting) return;

    cancelAnimationFrame(this.requestID);
    this.startTime = 0;
    this.isStarting = false;
    this.oscillator.stop();
    this.clear();

    if (this.grass) {
      this.grass.startTime = 0;
      this.grass.draw();
    }
  }
  pause() {
    if (!this.isStarting) return;

    this.isPaused = true;
    this.oscillator.stop();
  }
  // ワイングラスセット
  grassSet() {
    if (!(this.grass instanceof Grass)) {
      this.grass = new Grass(this.dist, 0, this);

      this.clear();
      if (this.setup) {
        this.grass.draw();
      } else {
        this.grass.load(() => {
          this.setup = true;
          this.grass.draw();
        });
      }
    } else {
      this.grass.sx = 0;
      this.grass.sy = 0;
      this.grass.isBroken = false;
      this.grass.breakingSound.pause();
      this.clear();
      this.grass.draw();
    }
  }
  // ワイングラス破壊
  break() {

    const event = new CustomEvent("wineGrassBroken", {
      bubbles: true,
    });
    this.canvas.dispatchEvent(event);
  }
  // サウンド生成
  makeOscillator() {
    this.oscillator = this.audioCtx.createOscillator();

    this.oscillator.type = "sine";
    this.oscillator.connect(this.gainNode);
    this.oscillator.frequency.value = this.freq;
  }
  // 波 : Asin(2π * (x/λ - t/T)) * exp(-x/2000)
  wave(x, t) {
    if (this.paramList.length) {
      const param = this.getWaveParam(x);
      this.setCurrentFreq(param.freq);
      this.currentAmp = param.amp;
    }

    return (
      this.currentAmp *
      Math.sin(Math.PI * 2 * (x / this.lambda - t / this.T)) *
      this.attenuation(x)
    );
  }
  // 減衰率
  attenuation(a) {
    return Math.exp((-a / this.halfAmplitudeDistance) * Math.log(2));
  }
  // ある地点の波のパラメータを取得
  getWaveParam(x) {
    const param = {
      freq: this.freq,
      amp: this.amp,
    };

    for (const item of this.paramList) {
      if (x >= item.x) {
        param.freq = item.freq;
        param.amp = item.amp;
        break;
      }
    }
    return param;
  }
  // tick
  tick(time) {
    if (!this.startTime) {
      this.startTime = time;
    }

    this.freqCounter = 0;
    this.ampCounter = 0;
    this.setCurrentFreq(this.freq);
    this.currentAmp = this.amp;

    const elapsedTime = (time - this.startTime) / 1000; // 経過時間

    this.clear();

    const waveHead = Math.min(
      (this.speed * elapsedTime) / this.cmpp,
      this.maxX
    ); // 波の先頭位置 [px]

    this.paramList.forEach((item, i) => {
      item.x = (elapsedTime - item.time) * this.speed; // 変調位置 [cm]
      if (item.x / this.cmpp >= this.maxX) {
        this.paramList[i] = null;
      }
    });
    this.paramList = this.paramList.filter(Boolean);

    if (this.showWave) {
      const y0 = this.wave(0, elapsedTime);

      this.ctx.beginPath();
      this.ctx.moveTo(-22, 0);
      this.ctx.quadraticCurveTo(-11, 0, 0, y0);

      for (let x = 1; x <= waveHead; x++) {
        const y = this.wave(x * this.cmpp, elapsedTime);
        this.ctx.lineTo(x, y);
      }
      this.ctx.stroke();
    }

    if (this.lastFreq !== this.freq || this.lastAmp !== this.amp) {
      this.paramList.push({
        x: 0,
        time: elapsedTime,
        freq: this.lastFreq,
        amp: this.lastAmp,
      });
      this.lastFreq = this.freq;
      this.lastAmp = this.amp;
    }

    if (waveHead >= this.grass.x) {
      this.grass.vibration(elapsedTime);
    }
    this.grass.draw();

    if (!this.isPaused) {
      this.requestID = requestAnimationFrame(this.tick.bind(this));
    } else {
      this.pausedTime = time;
    }
  }
  // 座標軸描画
  line() {
    this.ctx.save();

    this.ctx.strokeStyle = "#000";
    this.ctx.lineWidth = 1;
    this.ctx.globalAlpha = .3;

    this.ctx.beginPath();
    this.ctx.moveTo(-paddingLeft, 0);
    this.ctx.lineTo(this.width, 0);
    this.ctx.moveTo(0, this.height / 2);
    this.ctx.lineTo(0, -this.height / 2);
    this.ctx.stroke();

    this.ctx.restore();
  }
  // キャンバスクリア
  clear() {
    this.ctx.clearRect(
      -paddingLeft,
      -this.height / 2,
      this.width,
      this.height
    );
    if (this.drawLine) this.line();
    this.grass.dx = 0;
  }
  // 周波数処理
  setCurrentFreq(f) {
    this.T = 1 / f;
    this.lambda = this.speed * this.T;
  }
  // Getter & Setter
  get freq() {
    return this._freq;
  }
  set freq(val) {
    this._freq = limit(val, this.minFreq, this.maxFreq);

    if (this.oscillator) {
      this.oscillator.frequency.value = this._freq;
    }
  }
  get amp() {
    return this._amp;
  }
  set amp(val) {
    this._amp = limit(val, 0, this.maxAmp);
    this.gainNode.gain.value = this._amp / this.maxAmp * 0.1
  }
  get dist() {
    return this._dist;
  }
  set dist(val) {
    this._dist = limit(val, 0, this.maxDist);
    if (this.grass) {
      this.grass.dist = this._dist;
      if (!this.isStarting) {
        this.clear();
        this.grass.draw();
      }
    }
  }
}

export default Simulator;
